import React, { useContext } from 'react';

import { locale as localeConfig } from '@adsk/au-common-js';
import cn from 'classnames';

import { useBeehiveQuery } from '../../../apis/beehive/hooks';
import CardGrid from '../../../components/presentational/card-grid/CardGrid';
import Skeleton from '../../../components/presentational/skeleton';
import languageOptions from '../../../helpers/languageOptions';
import { RelatedClassCard } from '../../../templates/class/components/related-class-card';
import { ConfigContext } from '../../contexts/ConfigContext';

export interface Props {
  // Keep records of older props in case backwards compatibility will be required
  // libraryId?: string;
  // parentId?: string;
  // categories?: string;
  baseURL?: string;
  locale: string;
  classIds?: Record<string, string>[];
  event?: string;
  year?: string;
  max?: number;
}
function FeaturedClasses({
  locale,
  classIds,
  baseURL,
  event,
  year,
  max = 3,
}: Props) {
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);
  const parsedBaseUrl = baseURL || COMMUNITY_FUNCTIONS_URL;

  const params: Record<string, string | number | boolean> = {
    locale,
    'fields.recordtype': 'class',
    pageSize: max,
    pageNum: 0,
    sort: 'created,desc',
  };
  if (classIds && classIds.length) {
    params['fields.id'] = classIds.map(entry => entry.id).join(',');
  } else {
    if (event?.trim()) params['fields.location'] = event.trim();
    if (year) params['fields.year'] = year;
  }

  const { data, isLoading } = useBeehiveQuery({
    baseURL: parsedBaseUrl,
    params,
  });
  const beehiveClassCards = data?.items?.length ? [...data.items] : [];

  const skeletons = Array.from({ length: max }).map((_, idx) => (
    <Skeleton
      /* eslint-disable-next-line react/no-array-index-key */
      key={`placeholder-${idx}`}
      width="100%"
      height={400}
      className={cn('auo-card')}
    />
  ));

  return (
    <CardGrid className="au-w-full">
      {isLoading
        ? skeletons
        : beehiveClassCards.map(item => {
            const { moduleType, units } = item.fields;
            const auLearningContentTypeName =
              units?.[0]?.fields?.auLearningContentType?.fields?.name ?? '';

            const classType =
              auLearningContentTypeName || moduleType || 'Class';
            return (
              <RelatedClassCard
                userProfileURL={parsedBaseUrl}
                userContentURL={parsedBaseUrl}
                key={item.id}
                classData={item}
                language={locale}
                header={classType}
              />
            );
          })}
    </CardGrid>
  );
}

// shared attribute definition for Block Editor and AURR
FeaturedClasses.attributes = {
  v: {
    type: 'integer',
  },
  baseURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com/services/',
  },
  // Used for background compatibility with v0.
  libraryId: {
    type: 'string',
    // eslint-disable-next-line no-secrets/no-secrets
    default: 'urn:adsk.content:library:78756fcf-e6f3-4184-abdd-04afc0337a02',
  },
  // Used for background compatibility with v0.
  parentId: {
    type: 'string',
    default: 'urn:adsk.content:node:eabd8321-4590-4d61-88c5-73ad65eba36a',
  },
  locale: {
    type: 'string',
    enum: localeConfig.urlLocales,
    default: localeConfig.defaultURLLocale,
    list: languageOptions,
  },
  event: {
    type: 'string',
    default: 'Virtual',
  },
  year: {
    type: 'string',
    default: '2021',
  },
  max: {
    type: 'string',
    default: '6',
  },
  classIds: {
    type: 'array',
    default: [],
  },
  categories: {
    type: 'array',
    default: [],
  },
  categoriesBaseUrl: {
    type: 'string',
  },
};

export default FeaturedClasses;
